import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    GppBadOutlined,
    PhoneIphoneOutlined,
    ShieldOutlined,
    LockPersonOutlined,
} from "@mui/icons-material";
import { LOBBY_EXIT_BUTTON_SX } from "../common/dialogActionButtonSx";
import { colors } from "../../theme/colors";
import { queueToastForNextLoad } from "../../stores/toastStore";
import TwoFactorAuthActivationStepsDialog from "./TwoFactorAuthActivationStepsDialog";
import TwoFactorAuthDisableDialog from "./TwoFactorAuthDisableDialog";
import {
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_MOBILE_MQ,
    ACCOUNT_PAGE_PREMIUM_CTA_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_ARROW_SX,
    ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX,
    ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX,
} from "./accountPageStyles";

const COLLAPSE_TIMEOUT = { enter: 320, exit: 260 };
const COLLAPSE_EASING = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const HOW_IT_WORKS_STEPS = [
    {
        num: "01",
        title: "Link an authenticator",
        description:
            "Scan a QR code with an app like Google Authenticator or Authy — it starts generating login codes instantly.",
    },
    {
        num: "02",
        title: "Confirm the pairing",
        description:
            "Enter the 6-digit code shown in your app once, so we know the setup worked correctly.",
    },
    {
        num: "03",
        title: "Save your recovery codes",
        description:
            "Store the one-time backup codes somewhere safe, in case you ever lose access to your device.",
    },
];

const FAQ_ITEMS = [
    {
        id: "what-is-2fa",
        question: "What is two-factor authentication?",
        answer:
            "It's a second login step in addition to your password. After you enter your password, you'll also enter a short code from your authenticator app, so a stolen password alone can't get into your account.",
    },
    {
        id: "supported-apps",
        question: "Which authenticator apps are supported?",
        answer:
            "Any app that supports the standard TOTP protocol works, including Google Authenticator, Authy, 1Password and Microsoft Authenticator.",
    },
    {
        id: "lost-device",
        question: "What if I lose my authenticator device?",
        answer:
            "Use one of your recovery codes to sign in, then re-link a new device from this page. Each recovery code works only once, so replace them once you've used a few.",
    },
];

const DISABLE_2FA_BUTTON_SX = {
    ...LOBBY_EXIT_BUTTON_SX,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "none",
    whiteSpace: "nowrap",
    minWidth: 0,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        fontSize: "0.88rem",
        minHeight: "2.35rem",
        padding: "0.55rem 1.15rem",
        letterSpacing: "0.04em",
        lineHeight: 1.25,
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
    "&:hover": {
        background: `
            linear-gradient(
                180deg,
                rgba(152, 38, 38, 0.98) 0%,
                rgba(104, 22, 22, 0.98) 100%
            )
        `,
        transform: "none",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.08) inset,
            0 -1px 0 rgba(0,0,0,0.32) inset,
            0 12px 28px rgba(0,0,0,0.42)
        `,
        "&::after": {
            opacity: 1,
        },
    },
    "&:active": {
        transform: "none",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.04) inset,
            0 4px 12px rgba(0,0,0,0.3)
        `,
    },
};

const STATUS_BAND_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    gap: { xs: "0.95rem", sm: "1.5rem" },
    width: "100%",
    mt: "1.75rem",
    px: { xs: "0.95rem", sm: "1.15rem" },
    py: { xs: "0.95rem", sm: "1.15rem" },
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: `
        linear-gradient(
            165deg,
            rgba(18,18,22,0.92) 0%,
            rgba(12,12,14,0.96) 52%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.primary.main, 0.35)}, transparent)`,
        pointerEvents: "none",
    },
};

const STATUS_BAND_CONTENT_SX = {
    display: "flex",
    flexDirection: "column",
    gap: { xs: "0.55rem", sm: "0" },
    minWidth: 0,
    flex: 1,
    width: "100%",
};

const STATUS_BAND_HEADER_SX = {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    gap: { xs: "0.7rem", sm: "1rem" },
    minWidth: 0,
};

const STATUS_BAND_ACTION_SX = {
    alignSelf: { xs: "stretch", sm: "center" },
    flexShrink: 0,
    width: { xs: "100%", sm: "auto" },
    minHeight: { xs: "2.35rem", sm: "2.65rem" },
    py: { xs: "0.55rem", sm: "0.65rem" },
    px: { xs: "1.15rem", sm: "1.35rem" },
    fontSize: { xs: "0.88rem", sm: "0.82rem" },
    lineHeight: 1.25,
    [ACCOUNT_PAGE_MOBILE_MQ]: {
        fontSize: "0.88rem",
        minHeight: "2.35rem",
        padding: "0.55rem 1.15rem",
        lineHeight: 1.25,
    },
};

const SHIELD_WRAP_SX = (enabled) => ({
    width: { xs: 38, sm: 52 },
    height: { xs: 38, sm: 52 },
    borderRadius: { xs: "10px", sm: "14px" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: enabled ? alpha(colors.state.success.main, 0.13) : "rgba(255,255,255,0.03)",
    border: `1px solid ${enabled ? alpha(colors.state.success.main, 0.35) : colors.border.default}`,
    color: enabled ? colors.state.success.main : colors.text.muted,
    transition: "background 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out",
});

const STATUS_PILL_SX = (enabled) => ({
    display: "inline-flex",
    alignItems: "center",
    px: "0.55rem",
    py: "0.2rem",
    borderRadius: "999px",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1.2,
    border: "1px solid",
    ...(enabled
        ? {
              color: colors.state.success.main,
              background: alpha(colors.state.success.main, 0.13),
              borderColor: alpha(colors.state.success.main, 0.35),
          }
        : {
              color: colors.primary.main,
              background: alpha(colors.primary.main, 0.1),
              borderColor: alpha(colors.primary.main, 0.3),
          }),
});

const SECTION_TITLE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.05rem", sm: "1.15rem" },
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.01em",
};

const SECTION_SUBTITLE_SX = {
    color: colors.text.muted,
    fontSize: "0.82rem",
    fontWeight: 400,
    lineHeight: 1.4,
    mt: "0.35rem",
};

const METHOD_TAG_SX = (variant) => ({
    display: "inline-flex",
    alignItems: "center",
    px: "0.45rem",
    py: "0.15rem",
    borderRadius: "5px",
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    lineHeight: 1.2,
    ...(variant === "recommended"
        ? {
              color: colors.primary.main,
              background: alpha(colors.primary.main, 0.14),
              border: `1px solid ${alpha(colors.primary.main, 0.35)}`,
          }
        : {
              color: colors.text.muted,
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${colors.border.default}`,
          }),
});

function TwoFactorCollapsibleSection({ id, title, subtitle, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    const contentId = `${id}-content`;
    const ArrowIcon = open ? KeyboardArrowUpRounded : KeyboardArrowDownRounded;

    return (
        <Box
            sx={{
                width: "100%",
                borderTop: `1px solid ${colors.border.subtle}`,
                pt: "1.5rem",
                pb: open ? 0 : "0.25rem",
            }}
        >
            <Typography component="h2" sx={{ m: 0, p: 0 }}>
                <Box
                    component="button"
                    type="button"
                    aria-expanded={open}
                    aria-controls={contentId}
                    onClick={() => setOpen((current) => !current)}
                    sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box component="span" sx={SECTION_TITLE_SX}>
                            {title}
                        </Box>
                        {subtitle ? (
                            <Typography component="span" sx={{ ...SECTION_SUBTITLE_SX, display: "block" }}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <ArrowIcon aria-hidden sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX} />
                </Box>
            </Typography>
            <Collapse in={open} timeout={COLLAPSE_TIMEOUT} easing={COLLAPSE_EASING}>
                <Box id={contentId} sx={{ pt: "1.1rem", pb: "1.75rem" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

TwoFactorCollapsibleSection.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node,
    defaultOpen: PropTypes.bool,
};

function FaqItem({ id, question, answer }) {
    const [open, setOpen] = useState(false);
    const answerId = `${id}-answer`;

    return (
        <Box
            sx={{
                borderTop: `1px solid ${colors.border.subtle}`,
                "&:first-of-type": { borderTop: "none" },
            }}
        >
            <Box
                component="button"
                type="button"
                aria-expanded={open}
                aria-controls={answerId}
                onClick={() => setOpen((current) => !current)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                    width: "100%",
                    m: 0,
                    p: "0.95rem 0",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: "inherit",
                    font: "inherit",
                    cursor: "pointer",
                    textAlign: "left",
                    "&:focus, &:focus-visible": { outline: "none" },
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        color: colors.text.primary,
                        fontSize: "0.87rem",
                        fontWeight: 500,
                        lineHeight: 1.4,
                    }}
                >
                    {question}
                </Typography>
                <KeyboardArrowDownRounded
                    aria-hidden
                    sx={{
                        color: colors.text.muted,
                        fontSize: "1.1rem",
                        flexShrink: 0,
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: `transform ${
                            open ? COLLAPSE_TIMEOUT.enter : COLLAPSE_TIMEOUT.exit
                        }ms ${open ? COLLAPSE_EASING.enter : COLLAPSE_EASING.exit}`,
                    }}
                />
            </Box>
            <Collapse in={open} timeout={COLLAPSE_TIMEOUT} easing={COLLAPSE_EASING}>
                <Typography
                    id={answerId}
                    sx={{
                        color: colors.text.secondary,
                        fontSize: "0.83rem",
                        lineHeight: 1.6,
                        pb: "1rem",
                        maxWidth: "37.5rem",
                    }}
                >
                    {answer}
                </Typography>
            </Collapse>
        </Box>
    );
}

FaqItem.propTypes = {
    id: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
};

function TwoFactorStatusBand({ enabled, onEnable, onDisable }) {
    return (
        <Box sx={STATUS_BAND_SX} aria-label="Two-factor authentication status">
            <Box sx={STATUS_BAND_CONTENT_SX}>
                <Box sx={STATUS_BAND_HEADER_SX}>
                    <Box sx={SHIELD_WRAP_SX(enabled)}>
                        <ShieldOutlined sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: "0.55rem",
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: { xs: "0.92rem", sm: "1.02rem" },
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                }}
                            >
                                Two-factor authentication
                            </Typography>
                            <Box component="span" sx={STATUS_PILL_SX(enabled)}>
                                {enabled ? "Active" : "Not active"}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Typography
                    sx={{
                        color: colors.text.secondary,
                        fontSize: { xs: "0.82rem", sm: "0.85rem" },
                        lineHeight: 1.5,
                        mt: { xs: 0, sm: "0.35rem" },
                        pl: { xs: 0, sm: "calc(52px + 1rem)" },
                    }}
                >
                    {enabled
                        ? "Your account is protected by your password and an authenticator app."
                        : "Your account is protected by your password only."}
                </Typography>
            </Box>

            {!enabled ? (
                <Box
                    component="button"
                    type="button"
                    onClick={onEnable}
                    sx={{
                        ...ACCOUNT_PAGE_PREMIUM_CTA_SX,
                        ...STATUS_BAND_ACTION_SX,
                    }}
                >
                    <ShieldOutlined sx={{ fontSize: "1rem", opacity: 0.92 }} />
                    Enable 2FA
                </Box>
            ) : (
                <Box
                    component="button"
                    type="button"
                    onClick={onDisable}
                    sx={{
                        ...DISABLE_2FA_BUTTON_SX,
                        ...STATUS_BAND_ACTION_SX,
                    }}
                >
                    <GppBadOutlined sx={{ fontSize: "1rem", opacity: 0.92 }} />
                    Disable 2FA
                </Box>
            )}
        </Box>
    );
}

TwoFactorStatusBand.propTypes = {
    enabled: PropTypes.bool.isRequired,
    onEnable: PropTypes.func,
    onDisable: PropTypes.func,
};

function HowItWorksContent() {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: { xs: "1.35rem", md: "1.5rem" },
                position: "relative",
            }}
        >
            {HOW_IT_WORKS_STEPS.map((step) => (
                <Box key={step.num} sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(255,255,255,0.03)",
                            border: `1px solid ${colors.border.default}`,
                            color: colors.primary.main,
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {step.num}
                    </Box>
                    <Typography
                        component="h3"
                        sx={{
                            color: colors.text.primary,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            lineHeight: 1.35,
                            mt: "0.85rem",
                        }}
                    >
                        {step.title}
                    </Typography>
                    <Typography
                        sx={{
                            color: colors.text.secondary,
                            fontSize: "0.81rem",
                            lineHeight: 1.55,
                            mt: "0.4rem",
                        }}
                    >
                        {step.description}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function ProtectionMethodRow({ icon: Icon, title, tag, tagVariant, description, actionLabel, enabled, onAction }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                py: "1rem",
                borderTop: `1px solid ${colors.border.subtle}`,
                "&:first-of-type": {
                    borderTop: "none",
                    pt: "0.25rem",
                },
            }}
        >
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    background: enabled
                        ? alpha(colors.state.success.main, 0.13)
                        : "rgba(255,255,255,0.03)",
                    border: `1px solid ${enabled ? alpha(colors.state.success.main, 0.35) : colors.border.default}`,
                    color: enabled ? colors.state.success.main : colors.text.secondary,
                }}
            >
                <Icon sx={{ fontSize: "1.15rem" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                    <Typography
                        component="span"
                        sx={{
                            color: colors.text.primary,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            lineHeight: 1.3,
                        }}
                    >
                        {title}
                    </Typography>
                    <Box component="span" sx={METHOD_TAG_SX(tagVariant)}>
                        {tag}
                    </Box>
                </Box>
                <Typography
                    sx={{
                        color: colors.text.muted,
                        fontSize: "0.8rem",
                        lineHeight: 1.45,
                        mt: "0.25rem",
                    }}
                >
                    {description}
                </Typography>
            </Box>
            {actionLabel ? (
                <Box
                    component="button"
                    type="button"
                    onClick={onAction}
                    sx={{
                        flexShrink: 0,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        color: colors.primary.main,
                        font: "inherit",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        px: "0.25rem",
                        py: "0.5rem",
                        "&:hover": { color: colors.accent.main },
                        "&:focus, &:focus-visible": { outline: "none" },
                    }}
                >
                    {actionLabel}
                </Box>
            ) : null}
        </Box>
    );
}

ProtectionMethodRow.propTypes = {
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    tagVariant: PropTypes.oneOf(["recommended", "muted"]),
    description: PropTypes.string.isRequired,
    actionLabel: PropTypes.string,
    enabled: PropTypes.bool,
    onAction: PropTypes.func,
};

export default function TwoFactorAuthPage({ user }) {
    const isEnabled = Boolean(user?.twoFactorEnabled);
    const [setupDialogOpen, setSetupDialogOpen] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);

    const handleOpenSetup = useCallback(() => {
        if (!isEnabled) {
            setSetupDialogOpen(true);
        }
    }, [isEnabled]);

    const handleOpenDisable = useCallback(() => {
        if (isEnabled) {
            setDisableDialogOpen(true);
        }
    }, [isEnabled]);

    const handleSetupComplete = useCallback(() => {
        queueToastForNextLoad({
            type: "success",
            message: "Two-factor authentication enabled",
        });
        window.location.reload();
    }, []);

    const handleDisableComplete = useCallback(() => {
        queueToastForNextLoad({
            type: "error",
            message: "Two-factor authentication disabled",
        });
        window.location.reload();
    }, []);

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Two-Factor Auth
                    </Typography>
                </Box>

                <TwoFactorStatusBand
                    enabled={isEnabled}
                    onEnable={handleOpenSetup}
                    onDisable={handleOpenDisable}
                />

                <Box sx={{ ...ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX, mt: "1.75rem", mb: 0 }} />

                <TwoFactorCollapsibleSection
                    id="two-factor-how-it-works"
                    title="How it works"
                    subtitle="Three short steps, roughly two minutes"
                >
                    <HowItWorksContent />
                </TwoFactorCollapsibleSection>

                <TwoFactorCollapsibleSection
                    id="two-factor-protection-methods"
                    title="Protection methods"
                    subtitle="What's currently securing your login"
                >
                    <ProtectionMethodRow
                        icon={PhoneIphoneOutlined}
                        title="Authenticator app"
                        tag={isEnabled ? "Connected" : "Recommended"}
                        tagVariant={isEnabled ? "muted" : "recommended"}
                        description={
                            isEnabled
                                ? "Connected — generating a fresh 6-digit code every 30 seconds."
                                : "Not connected — generates a fresh 6-digit code every 30 seconds."
                        }
                        actionLabel={isEnabled ? null : "Set up"}
                        enabled={isEnabled}
                        onAction={handleOpenSetup}
                    />
                    <ProtectionMethodRow
                        icon={LockPersonOutlined}
                        title="Recovery codes"
                        tag="Backup"
                        tagVariant="muted"
                        description={
                            isEnabled
                                ? "Generated automatically once 2FA is active."
                                : "Generated automatically once 2FA is active."
                        }
                        enabled={isEnabled}
                    />
                </TwoFactorCollapsibleSection>

                <TwoFactorCollapsibleSection
                    id="two-factor-faq"
                    title="Frequently asked questions"
                    subtitle="Common questions about two-factor authentication"
                >
                    <Box sx={{ pt: "0.15rem" }}>
                        {FAQ_ITEMS.map((item) => (
                            <FaqItem
                                key={item.id}
                                id={item.id}
                                question={item.question}
                                answer={item.answer}
                            />
                        ))}
                    </Box>
                </TwoFactorCollapsibleSection>
            </Box>

            <TwoFactorAuthActivationStepsDialog
                open={setupDialogOpen}
                onClose={() => setSetupDialogOpen(false)}
                onComplete={handleSetupComplete}
            />

            <TwoFactorAuthDisableDialog
                open={disableDialogOpen}
                onClose={() => setDisableDialogOpen(false)}
                onDisabled={handleDisableComplete}
            />
        </Box>
    );
}

TwoFactorAuthPage.propTypes = {
    user: PropTypes.object,
};
